import type { DropdownProps } from "@amzn/storm-ui";
import { Dropdown, InlineAlert, SearchInput } from "@amzn/storm-ui";
import React from "react";
import styled from "styled-components";

import type { DropdownItemData } from "./DropdownHelpers";
import { renderDropdownItems } from "./DropdownHelpers";
import { LabelEndComponent } from "./LabelEndComponent";

const LoadingDiv = styled.div`
    justify-content: center;
    display: flex;
    role: alert;
    aria-live: assertive;
`;

const StickyDiv = styled.div`
    position: sticky;
    top: -3px;
    margin-top: -3px;
    padding: 5px;
    z-index: 100;
    background: white;
`;

export interface DropdownComponentProps {
    dimension: string;
    loading?: boolean;
    showItemsWhileLoading?: boolean;
    search: string;
    items: DropdownItemData[];
    onChange: (e: string) => void;
    onEndScroll?: () => void;
    setSearch: (e: string) => void;
    label: string;
    selectedValue: string;
    tooltip?: string;
    disabled?: boolean;
    showSearchInput?: boolean;
    optional?: boolean;
    useFilterSearch?: boolean;
    missingText?: string;
}

export const DropdownComponent: React.FunctionComponent<
    DropdownComponentProps
> = (props) => {
    const {
        loading = false,
        showItemsWhileLoading = true,
        dimension,
        search,
        setSearch,
        items,
        onEndScroll,
        onChange,
        label,
        selectedValue,
        disabled,
        tooltip,
        showSearchInput = true,
        optional,
        useFilterSearch = false,
        missingText,
    } = props;

    const filteredItems = React.useMemo(
        () =>
            search.length > 0 && useFilterSearch
                ? items.filter(
                      ({ label, value }) =>
                          label?.toLowerCase().includes(search.toLowerCase()) ||
                          value?.toLowerCase().includes(search.toLowerCase()),
                  )
                : items,
        [search, items],
    );

    const { renderItems } = renderDropdownItems(
        filteredItems,
        loading,
        onEndScroll,
        missingText,
    );
    const conditionalParameters = {} as DropdownProps;
    const labelEndProps = { tooltip, optional };
    conditionalParameters.renderLabelEnd = (
        <LabelEndComponent {...labelEndProps} />
    );
    return (
        <Dropdown
            label={label}
            onOverrideLabel={(item, value) => (value && item) ?? ""}
            selectedValue={selectedValue}
            data-testid={`${dimension}-dropdown`}
            preRender={() => (
                <StickyDiv>
                    {showSearchInput && (
                        <SearchInput
                            id={`${dimension}-search`}
                            data-testid={`${dimension}-search`}
                            onClear={() => {
                                setSearch("");
                            }}
                            value={search}
                            fullWidth
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                        />
                    )}
                </StickyDiv>
            )}
            postRender={() =>
                loading && (
                    <LoadingDiv>
                        <InlineAlert
                            message="Loading"
                            data-testid="dropdown-loading"
                            style={{ textAlign: "center" }}
                        />
                    </LoadingDiv>
                )
            }
            onChange={onChange}
            disabled={disabled}
            fullWidth
            {...conditionalParameters}
        >
            {(loading && showItemsWhileLoading && renderItems) ||
                (!loading && renderItems)}
        </Dropdown>
    );
};
